// v2 (2026-08-02): the backend's substantive-only dependency identity
// (campaign_microsite_dependency_v2). The portals validate the exact version
// they were built against — an unrecognized version fails closed as a
// governance-interpretation error, never as proof the strategy is stale.
export const CAMPAIGN_MICROSITE_GOVERNANCE_SCHEMA_VERSION =
  "campaign_microsite_dependency_v2";
export const CAMPAIGN_MICROSITE_DYNAMIC_PUBLICATION_SCHEMA_VERSION = "v4";

export const resolveCampaignMicrositePayloadForRequest = (
  responseState,
  activeRequestKey
) => {
  const expectedKey = String(activeRequestKey || "").trim();
  return String(responseState?.requestKey || "").trim() === expectedKey
    ? responseState?.payload || null
    : null;
};

export const canRegisterCampaignMicrositePageAwareness = ({
  loading = false,
  error = "",
  micrositeContext = null,
  canRenderStrategy = false,
} = {}) =>
  loading !== true &&
  !String(error || "").trim() &&
  Boolean(micrositeContext) &&
  canRenderStrategy === true;

const GOVERNANCE_STATUSES = new Set([
  "CURRENT",
  "REGENERATION_REQUIRED",
  "ALLOCATION_APPROVAL_REQUIRED",
  "AWAITING_APPROVAL",
]);
/*
 * hasIdentity/isSha256 lived here to re-validate the governed identity hashes
 * this surface no longer receives or re-checks. The authority publishes the
 * verdict; the render gates carry it. Removed with the re-derivation.
 */

const parseDateDay = (value) => {
  const match = String(value || "")
    .trim()
    .match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const timestamp = Date.UTC(year, month - 1, day);
  const date = new Date(timestamp);
  if (
    date.getUTCFullYear() !== year ||
    date.getUTCMonth() !== month - 1 ||
    date.getUTCDate() !== day
  ) {
    return null;
  }
  return Math.floor(timestamp / 86400000);
};

const formatDateDay = (epochDay) =>
  Number.isInteger(epochDay)
    ? new Date(epochDay * 86400000).toISOString().slice(0, 10)
    : "";

const SHORT_MONTHS = Object.freeze([
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
]);

const formatShortDateDay = (epochDay) => {
  if (!Number.isInteger(epochDay)) return "";
  const date = new Date(epochDay * 86400000);
  return `${SHORT_MONTHS[date.getUTCMonth()]} ${date.getUTCDate()}`;
};

const formatShortDateRange = (startDay, endDay) =>
  `${formatShortDateDay(startDay)} – ${formatShortDateDay(endDay)}`;

export const buildCampaignMicrositeCalendarWeekRanges = (flightStart, flightEnd) => {
  const startDay = parseDateDay(flightStart);
  const endDay = parseDateDay(flightEnd);
  if (startDay === null || endDay === null || endDay < startDay) return [];

  const ranges = [];
  let cursorDay = startDay;
  while (cursorDay <= endDay) {
    const dayOfWeek = new Date(cursorDay * 86400000).getUTCDay();
    const daysSinceMonday = (dayOfWeek + 6) % 7;
    const calendarWeekEndDay = cursorDay + (6 - daysSinceMonday);
    const rangeEndDay = Math.min(calendarWeekEndDay, endDay);
    ranges.push({
      startDate: formatDateDay(cursorDay),
      endDate: formatDateDay(rangeEndDay),
      startDay: cursorDay,
      endDay: rangeEndDay,
    });
    cursorDay = rangeEndDay + 1;
  }
  return ranges;
};

const toMoneyCents = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const normalized =
    typeof value === "number"
      ? value
      : Number(String(value).replace(/[$,\s]/g, "").replace(/^\((.*)\)$/, "-$1"));
  return Number.isFinite(normalized)
    ? Math.round((normalized + Number.EPSILON) * 100)
    : null;
};

const resolvePlatformKey = (value) => {
  const raw =
    value && typeof value === "object"
      ? value.key ||
        value.platformKey ||
        value.platform ||
        value.platformLabel ||
        value.label ||
        value.name ||
        value.title
      : value;
  const normalized = String(raw || "")
    .trim()
    .toLowerCase()
    .replace(/&/g, " and ")
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
  if (
    [
      "meta",
      "facebook",
      "instagram",
      "facebook_ads",
      "instagram_ads",
      "meta_reels",
      "meta_feed",
      "instagram_reels",
      "instagram_stories",
    ].includes(normalized)
  ) {
    return "meta";
  }
  if (["display", "display_retargeting", "retargeting"].includes(normalized)) {
    return "display";
  }
  if (["connected_tv", "connectedtv", "ctv", "ott"].includes(normalized)) {
    return "ctv";
  }
  return normalized;
};

const containsOutOfFlightWeekReference = (value, weekCount) => {
  const text = String(value || "");
  for (const match of text.matchAll(
    /\bweeks?\s*(\d+)\s*(?:[-–—]|to|through)\s*(\d+)\b/gi
  )) {
    if (Math.max(Number(match[1]), Number(match[2])) > weekCount) return true;
  }
  for (const match of text.matchAll(/\bweeks?\s*(\d+)\b/gi)) {
    if (Number(match[1]) > weekCount) return true;
  }
  for (const match of text.matchAll(/\b(\d+)\s*[- ]week(?:s|\s+ramp)?\b/gi)) {
    if (Number(match[1]) > weekCount) return true;
  }
  return /\bsix[ -]?week(?:s|\s+ramp)?\b/i.test(text) && weekCount < 6;
};

const invalidCalendar = (code, reason) => ({ valid: false, code, reason });

export const validateCampaignMicrositeCalendar = ({ calendar, current } = {}) => {
  const resolved = calendar?.launchActivationCalendar || calendar;
  if (!resolved || typeof resolved !== "object") {
    return invalidCalendar("missing_launch_calendar", "The governed launch calendar is missing.");
  }

  const flightStart = String(current?.flightStart || "").trim();
  const flightEnd = String(current?.flightEnd || "").trim();
  const flightStartDay = parseDateDay(flightStart);
  const flightEndDay = parseDateDay(flightEnd);
  const inclusiveDayCount = Number(current?.inclusiveDayCount);
  const weekCount = Number(current?.weekCount);
  const expectedWeekRanges = buildCampaignMicrositeCalendarWeekRanges(
    flightStart,
    flightEnd
  );
  if (
    flightStartDay === null ||
    flightEndDay === null ||
    flightEndDay < flightStartDay ||
    !Number.isInteger(inclusiveDayCount) ||
    inclusiveDayCount !== flightEndDay - flightStartDay + 1 ||
    !Number.isInteger(weekCount) ||
    weekCount < 1 ||
    weekCount !== expectedWeekRanges.length
  ) {
    return invalidCalendar(
      "invalid_current_flight",
      "The governed flight identity is invalid."
    );
  }

  if (
    !Number.isInteger(resolved.durationWeeks) ||
    resolved.durationWeeks !== weekCount ||
    String(resolved.durationBadge || "").trim() !== `${weekCount}-WEEK RAMP` ||
    String(resolved.flightStartDate || "").trim() !== flightStart ||
    String(resolved.flightEndDate || "").trim() !== flightEnd ||
    !Number.isInteger(resolved.inclusiveDayCount) ||
    resolved.inclusiveDayCount !== inclusiveDayCount
  ) {
    return invalidCalendar(
      "calendar_flight_identity_mismatch",
      "The launch calendar does not match the governed flight identity."
    );
  }

  const spendRamp = resolved.spendRamp;
  const weeks = Array.isArray(spendRamp?.weeks) ? spendRamp.weeks : [];
  const platforms = Array.isArray(spendRamp?.platforms) ? spendRamp.platforms : [];
  const representedBudgetCents = toMoneyCents(spendRamp?.representedBudget);
  if (
    weeks.length !== weekCount ||
    !platforms.length ||
    representedBudgetCents === null ||
    representedBudgetCents < 0
  ) {
    return invalidCalendar(
      "calendar_reconciliation_contract_missing",
      "The launch calendar is missing governed reconciliation evidence."
    );
  }

  const declaredPlatformTotals = new Map();
  for (const platform of platforms) {
    const key = resolvePlatformKey(platform);
    const totalCents = toMoneyCents(platform?.total);
    if (!key || totalCents === null || totalCents < 0 || declaredPlatformTotals.has(key)) {
      return invalidCalendar(
        "invalid_platform_total",
        "The launch calendar has invalid platform-total authority."
      );
    }
    declaredPlatformTotals.set(key, totalCents);
  }
  const declaredTotalCents = [...declaredPlatformTotals.values()].reduce(
    (sum, value) => sum + value,
    0
  );
  if (declaredTotalCents !== representedBudgetCents) {
    return invalidCalendar(
      "platform_totals_do_not_reconcile",
      "Platform totals do not reconcile to the represented budget."
    );
  }

  const observedPlatformTotals = new Map(
    [...declaredPlatformTotals.keys()].map((key) => [key, 0])
  );
  let observedWeeklyTotalCents = 0;

  for (let index = 0; index < weeks.length; index += 1) {
    const week = weeks[index] || {};
    const expectedRange = expectedWeekRanges[index];
    const weekTotalCents = toMoneyCents(week.total);
    const expectedWeekNumber = index + 1;
    const expectedDateRange = formatShortDateRange(
      expectedRange.startDay,
      expectedRange.endDay
    );
    if (
      String(week.key || "").trim() !== `week_${expectedWeekNumber}` ||
      Number(week.index) !== expectedWeekNumber ||
      String(week.label || "").trim() !== `Week ${expectedWeekNumber}` ||
      String(week.startDate || "").trim() !== expectedRange.startDate ||
      String(week.endDate || "").trim() !== expectedRange.endDate ||
      String(week.dateRange || "").trim() !== expectedDateRange ||
      Number(week.inclusiveDayCount) !==
        expectedRange.endDay - expectedRange.startDay + 1 ||
      weekTotalCents === null ||
      weekTotalCents < 0 ||
      containsOutOfFlightWeekReference(week.label, weekCount)
    ) {
      return invalidCalendar(
        "invalid_week_boundary",
        "A launch-calendar week falls outside the governed flight."
      );
    }

    const segments = Array.isArray(week.segments) ? week.segments : [];
    if (segments.length !== declaredPlatformTotals.size) {
      return invalidCalendar(
        "incomplete_week_platform_matrix",
        "Every governed platform must appear exactly once in every week."
      );
    }
    const observedWeekPlatforms = new Set();
    let observedWeekTotalCents = 0;
    for (const segment of segments) {
      const key = resolvePlatformKey(segment);
      const valueCents = toMoneyCents(segment?.value);
      if (
        !key ||
        !declaredPlatformTotals.has(key) ||
        observedWeekPlatforms.has(key) ||
        valueCents === null ||
        valueCents < 0
      ) {
        return invalidCalendar(
          "invalid_week_segment",
          "A launch-calendar platform segment is invalid or duplicated."
        );
      }
      observedWeekPlatforms.add(key);
      observedWeekTotalCents += valueCents;
      observedPlatformTotals.set(key, observedPlatformTotals.get(key) + valueCents);
    }
    if (observedWeekTotalCents !== weekTotalCents) {
      return invalidCalendar(
        "week_total_does_not_reconcile",
        "A launch-calendar week does not reconcile to its platform segments."
      );
    }
    observedWeeklyTotalCents += weekTotalCents;
  }

  if (observedWeeklyTotalCents !== representedBudgetCents) {
    return invalidCalendar(
      "represented_budget_does_not_reconcile",
      "Weekly launch spend does not reconcile to the represented budget."
    );
  }
  for (const [key, declaredTotal] of declaredPlatformTotals.entries()) {
    if (observedPlatformTotals.get(key) !== declaredTotal) {
      return invalidCalendar(
        "platform_segment_total_does_not_reconcile",
        "Platform launch spend does not reconcile to governed platform totals."
      );
    }
  }

  const hasCampaignBudget =
    spendRamp?.campaignBudget !== null &&
    spendRamp?.campaignBudget !== undefined &&
    spendRamp?.campaignBudget !== "";
  const hasUnrepresentedBudget =
    spendRamp?.unrepresentedBudget !== null &&
    spendRamp?.unrepresentedBudget !== undefined &&
    spendRamp?.unrepresentedBudget !== "";
  if (hasCampaignBudget || hasUnrepresentedBudget) {
    const campaignBudgetCents = toMoneyCents(spendRamp?.campaignBudget);
    const unrepresentedBudgetCents = toMoneyCents(spendRamp?.unrepresentedBudget);
    if (
      campaignBudgetCents === null ||
      campaignBudgetCents < 0 ||
      unrepresentedBudgetCents === null ||
      unrepresentedBudgetCents < 0 ||
      representedBudgetCents + unrepresentedBudgetCents !== campaignBudgetCents
    ) {
      return invalidCalendar(
        "campaign_budget_does_not_reconcile",
        "Represented and unrepresented spend do not reconcile to the campaign budget."
      );
    }
  }

  const milestones = Array.isArray(resolved.milestones) ? resolved.milestones : [];
  for (const milestone of milestones) {
    const milestoneStartDay = parseDateDay(milestone?.startDate);
    const milestoneEndDay = parseDateDay(milestone?.endDate);
    const firstWeekIndex = expectedWeekRanges.findIndex(
      (range) =>
        milestoneStartDay !== null &&
        milestoneStartDay >= range.startDay &&
        milestoneStartDay <= range.endDay
    );
    const finalWeekIndex = expectedWeekRanges.findIndex(
      (range) =>
        milestoneEndDay !== null &&
        milestoneEndDay >= range.startDay &&
        milestoneEndDay <= range.endDay
    );
    const expectedWeekLabel =
      firstWeekIndex === finalWeekIndex
        ? `Week ${firstWeekIndex + 1}`
        : `Week ${firstWeekIndex + 1}–${finalWeekIndex + 1}`;
    const expectedDateRange = formatShortDateRange(
      milestoneStartDay,
      milestoneEndDay
    );
    if (
      String(milestone?.scope || "").trim().toUpperCase() !== "IN_FLIGHT" ||
      milestoneStartDay === null ||
      milestoneEndDay === null ||
      firstWeekIndex < 0 ||
      finalWeekIndex < firstWeekIndex ||
      milestoneStartDay < flightStartDay ||
      milestoneEndDay > flightEndDay ||
      milestoneEndDay < milestoneStartDay ||
      String(milestone?.weekLabel || "").trim() !== expectedWeekLabel ||
      String(milestone?.dateRange || "").trim() !== expectedDateRange ||
      containsOutOfFlightWeekReference(
        [milestone?.weekLabel, milestone?.urgency, milestone?.title, milestone?.copy]
          .filter(Boolean)
          .join(" "),
        weekCount
      )
    ) {
      return invalidCalendar(
        "milestone_outside_flight",
        "An activation milestone falls outside the governed in-flight scope."
      );
    }
  }

  return {
    valid: true,
    code: "current",
    reason: "",
    flightStart,
    flightEnd,
    inclusiveDayCount,
    weekCount,
    representedBudgetCents,
    expectedWeekRanges: expectedWeekRanges.map(({ startDate, endDate }) => ({
      startDate,
      endDate,
    })),
  };
};

const resolveCampaignIdentity = (campaign) => ({
  campaignId: campaign?.id ?? campaign?.campaignId ?? campaign?.campaign_id,
  advertiserId:
    campaign?.advertiserId ?? campaign?.advertiser_id ?? campaign?.advertiser?.id,
  flightStart:
    campaign?.flightStartDate ??
    campaign?.flight_start_date ??
    campaign?.flightStart ??
    campaign?.flight_start,
  flightEnd:
    campaign?.flightEndDate ??
    campaign?.flight_end_date ??
    campaign?.flightEnd ??
    campaign?.flight_end,
  platforms: Array.isArray(campaign?.platforms) ? campaign.platforms : [],
  budget:
    campaign?.totalBudget ??
    campaign?.total_budget ??
    campaign?.campaignBudget ??
    campaign?.campaign_budget,
});

// Normalizes a governance findings value into an array of { code } objects.
// Accepts the canonical string-code array, object findings ({ code } /
// { findingCode }), a single finding object, or a JSON-encoded array; every
// unsupported shape normalizes to [] rather than poisoning the whole
// response. Findings are advisory display evidence — status stays
// authoritative and is never derived from findings.
export const normalizeCampaignMicrositeGovernanceFindings = (value) => {
  let source = value;
  if (typeof source === "string") {
    const text = source.trim();
    if (text.startsWith("[") || text.startsWith("{")) {
      try {
        source = JSON.parse(text);
      } catch (_error) {
        return [];
      }
    } else {
      source = text ? [text] : [];
    }
  }
  if (source && typeof source === "object" && !Array.isArray(source)) source = [source];
  if (!Array.isArray(source)) return [];
  return source
    .map((entry) => {
      if (typeof entry === "string") {
        const code = entry.trim();
        return code ? { code } : null;
      }
      if (entry && typeof entry === "object") {
        const code = String(entry.code ?? entry.findingCode ?? entry.finding_code ?? "").trim();
        return code ? { ...entry, code } : null;
      }
      return null;
    })
    .filter(Boolean);
};

/*
 * REBIND_REQUIRED is a third governance state.
 *
 * Surfaces used to derive "is this stale" from one boolean and treat its
 * absence as "current". That is wrong for a cosmetic rebind: the strategy is
 * not stale, but the stored stamp still names the old label, so it is not
 * current either until the reconciliation write happens. Deriving this here
 * keeps both portals answering the question the same way.
 */
export const resolveStrategyRebindState = (governanceStamp) => {
  if (!governanceStamp || typeof governanceStamp !== "object") {
    return { needsRebind: false, isStale: false, aiRequired: false };
  }
  const status = String(governanceStamp.status || "").trim().toUpperCase();
  const reasonCode = String(governanceStamp.reasonCode || "").trim().toUpperCase();
  const isStale =
    governanceStamp.requiresRegeneration === true || status === "REGENERATION_REQUIRED";
  const needsRebind =
    !isStale && (status === "REBIND_REQUIRED" || reasonCode === "COSMETIC_REBIND_REQUIRED");
  return {
    needsRebind,
    isStale,
    // Explicit, so no surface has to infer it from a status string.
    aiRequired: governanceStamp.aiRequired === true || isStale,
  };
};

// Reason-specific internal remediation copy.
//
// `status` alone cannot distinguish a strategy whose governed inputs genuinely
// changed from one that was never bound at all — both surface as
// REGENERATION_REQUIRED. Until 2026-08-06 every such state claimed the saved
// strategy was "stale relative to the current campaign", which sent operators
// to diff a campaign that had not moved: strategies generated before the
// governance system shipped (#101, 2026-08-02) carry no binding stamp, so they
// report REGENERATION_REQUIRED with their inputs provably unchanged.
//
// These strings are INTERNAL. The client microsite must never render them.
export const CAMPAIGN_MICROSITE_INTERNAL_REMEDIATION_COPY = Object.freeze({
  // A cosmetic rebind is a STAMP, not a generation. Renaming a campaign once
  // billed a full strategy regeneration, and this copy is where an operator
  // learns that it no longer does. Without an entry here the reasonCode falls
  // through to the default below, which says "Regeneration Required" — the
  // exact sentence the backend change exists to stop showing.
  COSMETIC_REBIND_REQUIRED: Object.freeze({
    label: "Rebind Required",
    reason:
      "Only cosmetic details changed since this strategy was generated, so it still describes the campaign. Rebind it to the current inputs — no regeneration is required.",
  }),
  // Withheld because nothing can prove this binding still describes the
  // campaign. Nothing proved it CHANGED either, and saying so sent operators
  // to diff a campaign that had not moved.
  LEGACY_BINDING_UNVERIFIABLE: Object.freeze({
    label: "Governed Binding Required",
    reason:
      "This strategy's binding predates recorded dependency values, so it cannot be verified against the current campaign either way. Adopt a governed binding to make it reviewable.",
  }),
  SUBSTANTIVE_DEPENDENCY_CHANGED: Object.freeze({
    label: "Regeneration Required",
    reason:
      "The campaign's governed inputs changed after this strategy was generated. Regenerate the strategy before submitting it for approval.",
  }),
  STRATEGY_BINDING_ABSENT: Object.freeze({
    label: "Governed Binding Required",
    reason:
      "This strategy predates governed microsite binding, so it carries no dependency binding. Its recorded inputs still match the current campaign — nothing is stale. Adopt a governed binding or regenerate the strategy.",
  }),
  // Labels stay the canonical status vocabulary wherever regeneration/repair
  // really is the remedy — only the REASON gets specific. STRATEGY_BINDING_ABSENT
  // is the one state that earns its own label, because regeneration is not the
  // only remedy there: an unchanged pre-governance strategy can be bound
  // without reproducing its content.
  INVALID_DEPENDENCY_MATERIAL: Object.freeze({
    label: "Regeneration Required",
    reason:
      "The campaign's governed inputs are incomplete, so no strategy can be verified against them. Complete the flight window and channel allocation before regenerating.",
  }),
  REGENERATION_OVERRIDE: Object.freeze({
    label: "Regeneration Required",
    reason:
      "Governance has marked this strategy for regeneration. Regenerate the strategy before submitting it for approval.",
  }),
  GOVERNANCE_EVIDENCE_INCOMPLETE: Object.freeze({
    label: "Regeneration Required",
    reason:
      "The governed evidence for this strategy is incomplete. Restore a complete governed binding before previewing it.",
  }),
});

const CAMPAIGN_MICROSITE_DEFAULT_REMEDIATION_COPY = Object.freeze({
  label: "Regeneration Required",
  reason: "This strategy could not be verified against the current campaign and has been withheld.",
});

export const resolveCampaignMicrositeRemediationCopy = (reasonCode) =>
  CAMPAIGN_MICROSITE_INTERNAL_REMEDIATION_COPY[
    String(reasonCode || "").trim().toUpperCase()
  ] || CAMPAIGN_MICROSITE_DEFAULT_REMEDIATION_COPY;

// Evidence-shape failures (the response could not be interpreted) versus
// substantive failures (the response was understood and reports a real
// mismatch). Only the former may be presented as "governance status
// unavailable"; the latter are genuine regeneration/repair conditions.
export const GOVERNANCE_INTERPRETATION_FAILURE_CODES = Object.freeze([
  "missing_governance",
  "malformed_governance",
  "malformed_governance_evidence",
]);

const invalidEvidence = ({ governance = null, status = null, code, reason }) => ({
  valid: false,
  code,
  reason,
  status,
  governance,
  current: governance?.current || null,
  strategy: governance?.strategy || null,
  publication: governance?.publication || null,
  clientSafeToRender: false,
  previewSafeToRender: false,
  calendarValidation: null,
});

/*
 * A flight identity built from the CAMPAIGN, for responses that no longer
 * carry governance.current. Day and week counts are derived from the flight
 * dates rather than read from evidence, so the calendar check keeps working
 * on the sanitized public contract.
 */
/*
 * A governed media flight is months, not millennia. parseDateDay accepts any
 * well-formed YYYY-MM-DD, so a campaign carrying an implausible flight end
 * would make the week-range builder allocate one object per calendar week
 * across the whole span — up to ~521,000 of them, per call. The old code never
 * reached this path, because such a payload was rejected by the identity
 * checks first; deriving the flight from the campaign reaches it directly.
 *
 * Beyond the bound the counts are simply not derived, so the calendar
 * validator fails closed on an invalid flight exactly as it would otherwise.
 */
const MAX_DERIVABLE_FLIGHT_DAYS = 1827; // ~5 years

const deriveFlightIdentity = (campaign) => {
  const identity = resolveCampaignIdentity(campaign);
  const flightStart = String(identity.flightStart || "").trim();
  const flightEnd = String(identity.flightEnd || "").trim();
  const startDay = parseDateDay(flightStart);
  const endDay = parseDateDay(flightEnd);
  if (
    startDay === null ||
    endDay === null ||
    endDay < startDay ||
    endDay - startDay > MAX_DERIVABLE_FLIGHT_DAYS
  ) {
    return { flightStart, flightEnd };
  }
  return {
    flightStart,
    flightEnd,
    inclusiveDayCount: endDay - startDay + 1,
    weekCount: buildCampaignMicrositeCalendarWeekRanges(flightStart, flightEnd).length,
  };
};

export const validateCampaignMicrositeGovernanceEvidence = ({
  governance,
  campaign,
  calendar,
} = {}) => {
  /*
   * THE BACKEND IS THE AUTHORITY. This function checks that its answer is
   * INTERPRETABLE; it no longer re-decides the answer.
   *
   * It used to re-derive the whole verdict here from ~20 evidence fields —
   * planVersion, budgetIdentity, channelAllocationIdentity, dependencyHash,
   * storedContentHash, publication identity, plus a campaign-identity and
   * launch-calendar comparison. That was a second opinion about a rule only
   * the backend can evaluate, and it was wrong at least once in a way that
   * mattered: it withheld Campaign 13's current, governed, $1.5M strategy —
   * every dependency value identical, allocation APPROVED — while the
   * authority that produced the envelope reported it READY_FOR_REVIEW.
   *
   * Those fields are also internal evidence: hashes, fingerprints, operation
   * and approval identifiers. Publishing them to an anonymous viewer so that
   * this function could re-check them was the reason the public microsite
   * response carried 78 forbidden keys (BE #485). They are gone from the
   * public contract, and the authenticated governance endpoint serves them to
   * a reader who is entitled to them.
   *
   * What remains is the render contract: schema version, a known status, and
   * the render gates. If those are coherent, the backend's verdict
   * stands as given. It still fails CLOSED — an uninterpretable envelope
   * withholds, exactly as before.
   */
  if (!governance || typeof governance !== "object") {
    return invalidEvidence({
      code: "missing_governance",
      reason: "Strategy governance evidence is missing.",
    });
  }

  const status = String(governance.status || "").trim().toUpperCase();
  if (
    governance.schemaVersion !== CAMPAIGN_MICROSITE_GOVERNANCE_SCHEMA_VERSION ||
    !GOVERNANCE_STATUSES.has(status) ||
    typeof governance.clientSafeToRender !== "boolean" ||
    typeof governance.previewSafeToRender !== "boolean"
  ) {
    return invalidEvidence({
      governance,
      status,
      code: "malformed_governance",
      reason: "Strategy governance evidence is malformed.",
    });
  }

  /*
   * Evidence blocks are OPTIONAL and are never required to render.
   *
   * The public response omits them. An authenticated surface that has also
   * read GET /:campaignId/strategy-governance may merge them in for display,
   * and they are passed through untouched when present — but nothing here
   * gates on them, so a caller who cannot see them is not treated as a
   * verification failure.
   */
  /*
   * A DECLARED fail-closed envelope is valid governance evidence in itself.
   *
   * An invalid-dependency stamp legitimately carries null identity hashes and
   * often no launch calendar at all, so running the content checks below would
   * mislabel a genuine governance state as a technical verification failure.
   * Content stays withheld either way — this only keeps the labeling truthful.
   */
  if (
    (status === "REGENERATION_REQUIRED" ||
      (status === "ALLOCATION_APPROVAL_REQUIRED" && governance.clientReviewSafeToRender !== true)) &&
    governance.clientSafeToRender === false &&
    governance.previewSafeToRender === false
  ) {
    return {
      valid: true,
      code:
        status === "ALLOCATION_APPROVAL_REQUIRED"
          ? "allocation_approval_required_evidence"
          : "regeneration_required_evidence",
      reason:
        status === "ALLOCATION_APPROVAL_REQUIRED"
          ? "The governed channel allocation is awaiting approval."
          : "The governed strategy binding requires regeneration.",
      status,
      governance,
      current: governance.current || null,
      strategy: governance.strategy || null,
      publication: governance.publication || null,
      clientSafeToRender: false,
      previewSafeToRender: false,
      calendarValidation: null,
    };
  }

  /*
   * When the response DOES carry the evidence block — a legacy envelope, or an
   * authenticated surface that merged in GET /:campaignId/strategy-governance
   * — the backend's PUBLISHED drift verdicts are honoured.
   *
   * This is not the re-derivation that was removed. dependencyHashMatches and
   * contentHashMatches are the authority's own answers; reading them is
   * agreeing with it, not second-guessing it. Raw hash equality is used only
   * as a fallback for envelopes that predate the published booleans, and it is
   * the NARROWER answer, so it can withhold but never open the gate.
   *
   * Absent evidence is not a drift signal. The sanitized public contract omits
   * these fields, and the render gates already carry the same verdict.
   */
  const strategyEvidence = governance.strategy;
  const publicationEvidence = governance.publication;
  if (strategyEvidence && typeof strategyEvidence === "object") {
    const dependencyBindingMatches =
      typeof strategyEvidence.dependencyHashMatches === "boolean"
        ? strategyEvidence.dependencyHashMatches === true
        : governance.current?.dependencyHash === strategyEvidence.sourceDependencyHash;
    const publicationBindsStrategy =
      !publicationEvidence ||
      typeof publicationEvidence !== "object" ||
      publicationEvidence.sourceStrategyContentHash === undefined ||
      publicationEvidence.sourceStrategyContentHash === strategyEvidence.contentHash;
    if (!dependencyBindingMatches || !publicationBindsStrategy) {
      return invalidEvidence({
        governance,
        status,
        code: "dependency_hash_mismatch",
        reason: "Strategy dependency hashes do not match the current campaign.",
      });
    }
    if (strategyEvidence.contentHashMatches === false) {
      return invalidEvidence({
        governance,
        status,
        code: "strategy_content_hash_mismatch",
        reason: "The stored strategy content no longer matches its governed source hash.",
      });
    }
  }

  /*
   * The launch calendar is still validated, because it is CONTENT coherence,
   * not a governance verdict: the activation cards a reader sees must fall
   * inside the campaign's flight. That check needs a flight identity, which
   * used to come from governance.current. When the response omits the
   * evidence block, the flight is taken from the campaign itself and the
   * day/week counts are derived from it — the same arithmetic the backend
   * publishes, applied to the same dates.
   */
  const calendarFlight = governance.current || deriveFlightIdentity(campaign);

  const calendarValidation = validateCampaignMicrositeCalendar({
    calendar,
    current: calendarFlight,
  });
  if (!calendarValidation.valid) {
    return invalidEvidence({
      governance,
      status,
      code: calendarValidation.code,
      reason: calendarValidation.reason,
    });
  }

  return {
    valid: true,
    code: "governed_render_state",
    reason: "",
    status,
    governance,
    current: governance.current || null,
    strategy: governance.strategy || null,
    publication: governance.publication || null,
    clientSafeToRender: governance.clientSafeToRender === true,
    previewSafeToRender: governance.previewSafeToRender === true,
    calendarValidation,
  };
};
